/**
 * ホーム画面への追加(PWAインストール)の状態管理
 *
 * 参加者にホーム画面へ置いてもらうのは、見た目の都合ではなく実利のため:
 *
 *   1. アイコンから直接起動できる。圏外の島でブラウザのURL入力は現実的でない。
 *   2. iOS Safari は「7日間開かれなかったサイト」の保存データを削除するが、
 *      ホーム画面のWebアプリはその対象外になる。準備した音声が消えにくくなる。
 *
 * beforeinstallprompt は読み込み直後に飛んでくる。画面の描画を待っていると
 * 取りこぼすため、このモジュールを main.ts から真っ先に読み込んで捕まえておく。
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPlatform = "ios" | "android" | "other";

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

addEventListener("beforeinstallprompt", (e) => {
  // 既定のミニバーを止め、こちらのタイミングで出す。
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  notify();
});

addEventListener("appinstalled", () => {
  deferred = null;
  installed = true;
  notify();
});

/** 追加できる状態が変わったら呼ばれる。解除関数を返す。 */
export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** ブラウザ標準の追加ダイアログを出せるか(Android Chrome 等) */
export function canPrompt(): boolean {
  return deferred !== null;
}

/**
 * すでにホーム画面から起動しているか。
 *
 * iOS Safari は display-mode を返さない時期が長かったので navigator.standalone も見る。
 */
export function isStandalone(): boolean {
  if (installed) return true;
  if (matchMedia("(display-mode: standalone)").matches) return true;
  if (matchMedia("(display-mode: fullscreen)").matches) return true;
  if ((navigator as { standalone?: boolean }).standalone === true) return true;
  return document.referrer.startsWith("android-app://");
}

/**
 * 案内の出し分けに使う端末種別。
 *
 * iPadOS 13 以降は既定で Macintosh を名乗るため、タッチ点数で見分ける。
 * ここを誤ると iPad に「⋮ をタップ」というAndroidの手順を見せることになる。
 */
export function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

/** 標準ダイアログを出す。出せない場合は "unavailable"。 */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const e = deferred;
  if (!e) return "unavailable";
  // prompt() は一度しか使えない。結果に関わらず手放す。
  deferred = null;
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    if (outcome === "accepted") installed = true;
    notify();
    return outcome;
  } catch {
    notify();
    return "unavailable";
  }
}
